import { ArrowShortDownIcon } from '@fourthwall/icons'
import find from 'lodash-es/find'
import findIndex from 'lodash-es/findIndex'
import isEmpty from 'lodash-es/isEmpty'
import type { ChangeEvent, FocusEvent, KeyboardEvent, ReactNode } from 'react'
import React, { useEffect, useRef, useState } from 'react'
import type { IconComponent } from '../../common/Icon'
import { Icon } from '../../common/Icon'
import { Loading } from '../../common/Loading'
import { VisuallyHidden } from '../../common/VisuallyHidden'
import { withFormikSupport } from '../../hocs/withFormikSupport'
import { useOutsideClick } from '../../hooks/useOutsideClick'
import { Options } from '../../partials/Options/Options'
import { Input } from '../Input'
import * as S from './styled'
import { KeyCode } from './consts'
import type { AutoCompleteProps, Option } from './types'

const AutoCompleteComponent = ({
  suggestion,
  options,
  size = 'large',
  placement = 'bottom',
  maxHeight = 300,
  isLoading = false,
  withArrow = false,
  optionsGap = 0,
  optionsLabel,
  emptyState,
  onSelect,
  onChange,
  onFocus,
  onBlur,
  onSearch,
  onPaste,
  ...other
}: AutoCompleteProps) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const [activeOptionId, setActiveOptionId] = useState('')
  const [isFocused, setIsFocused] = useState(false)
  const [isSelectingOption, setIsSelectingOption] = useState(false)

  const changeActiveOption = (number: number) => {
    if (options && !isEmpty(options)) {
      const currentOptionIndex = findIndex(options, { id: activeOptionId })
      const lastOption = options[options.length - 1]
      const firstOption = options[0]

      if (number === 1) {
        if (!activeOptionId || activeOptionId === lastOption.id) {
          return setActiveOptionId(firstOption.id)
        }

        return setActiveOptionId(options[currentOptionIndex + 1].id)
      }

      if (number === -1) {
        if (!activeOptionId || activeOptionId === firstOption.id) {
          return setActiveOptionId(lastOption.id)
        }

        return setActiveOptionId(options[currentOptionIndex - 1].id)
      }
    }

    return null
  }

  useEffect(() => {
    if (options && options.length && (isFocused || isSelectingOption)) {
      setActiveOptionId(options[0].id)
    }
  }, [options, isFocused, isSelectingOption])

  useOutsideClick(containerRef, () => {
    setIsFocused(false)
    setIsSelectingOption(false)
  })

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (onChange) {
      onChange(e)
    }

    setIsSelectingOption(true)

    if (isFocused && onSearch) {
      onSearch(e.target.value)
    }
  }

  const handleInputFocus = (e: FocusEvent<HTMLInputElement>) => {
    if (onFocus) {
      onFocus(e)
    }

    setIsFocused(true)
  }

  const handleInputBlur = (e: FocusEvent<HTMLInputElement>) => {
    if (onBlur && !isSelectingOption) {
      onBlur(e)
    }

    setIsFocused(false)
  }

  const handleOptionSelect = (option: Option) => {
    setIsFocused(false)
    setIsSelectingOption(false)

    if (onSelect) {
      onSelect(option)
    }
  }

  const handleStartSelectingOption = () => {
    setIsSelectingOption(true)
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    switch (e.keyCode) {
      case KeyCode.ArrowDown:
        e.preventDefault()
        changeActiveOption(1)
        break
      case KeyCode.ArrowUp:
        e.preventDefault()
        changeActiveOption(-1)
        break
      case KeyCode.Escape:
        e.preventDefault()
        setIsFocused(false)
        break
      case KeyCode.Tab:
        setIsFocused(false)
        break
      case KeyCode.Enter:
        if (activeOptionId) {
          e.preventDefault()

          const selectedOption = find(options, {
            id: activeOptionId,
          })

          if (selectedOption) {
            handleOptionSelect(selectedOption)
          }
        }
        break
      default:
    }
  }

  const renderOption = (option: Option) => (
    <Options.Item
      key={option.id}
      label={option.content}
      active={option.id === activeOptionId}
      onClick={() => handleOptionSelect(option)}
    />
  )

  const renderSelectedOption = () => {
    const selectedOption = find(options, { id: activeOptionId })

    if (!selectedOption) return

    return (
      <VisuallyHidden>
        <div aria-live="polite" aria-atomic="true">
          {selectedOption.content}
        </div>
      </VisuallyHidden>
    )
  }

  const renderOptions = () => {
    const containerProps = {
      $size: size,
      $placement: placement,
      $withError: !!other.error,
      onMouseDown: handleStartSelectingOption,
      onTouchStart: handleStartSelectingOption,
    }

    if (!options?.length && isLoading && isFocused) {
      return (
        <S.OptionsContainer {...containerProps}>
          {optionsLabel && <S.OptionsLabel>{optionsLabel}</S.OptionsLabel>}
          <Loading isLoading>
            <S.OptionsWrapper $maxHeight={maxHeight} $withOptionsLabel={!!optionsLabel}>
              <Options gap={optionsGap}>
                <div style={{ height: 150 }} />
              </Options>
            </S.OptionsWrapper>
          </Loading>
        </S.OptionsContainer>
      )
    }

    if (options && options.length && (isFocused || isSelectingOption)) {
      return (
        <S.OptionsContainer {...containerProps}>
          {optionsLabel && <S.OptionsLabel>{optionsLabel}</S.OptionsLabel>}
          <Loading isLoading={isLoading}>
            <S.OptionsWrapper $maxHeight={maxHeight} $withOptionsLabel={!!optionsLabel}>
              <Options borderless gap={optionsGap}>
                {options.map(renderOption)}
              </Options>
            </S.OptionsWrapper>
            {renderSelectedOption()}
          </Loading>
        </S.OptionsContainer>
      )
    }

    if (!options?.length && other.value?.length && isFocused && emptyState) {
      return (
        <S.OptionsContainer {...containerProps}>
          <S.EmptyStateWrapper $height={maxHeight}>{emptyState}</S.EmptyStateWrapper>
        </S.OptionsContainer>
      )
    }

    return null
  }

  const getActionIconProps = () => {
    if (withArrow) {
      return {
        actionIcon: <Icon component={ArrowShortDownIcon} height={16} />,
        onActionIconClick: () => setIsFocused((prevIsFocused) => !prevIsFocused),
      }
    }

    return {
      actionIcon: 'actionIcon' in other ? other.actionIcon : undefined,
      onActionIconClick: 'onActionIconClick' in other ? other.onActionIconClick : undefined,
    } as {
      actionIcon?: IconComponent | ReactNode
      onActionIconClick?: () => void
    }
  }

  return (
    <S.Container ref={containerRef}>
      <Input
        {...other}
        inputRef={inputRef}
        role="combobox"
        suggestion={suggestion}
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        onKeyDown={handleKeyDown}
        onBlur={handleInputBlur}
        onPaste={onPaste}
        {...getActionIconProps()}
      />
      {renderOptions()}
    </S.Container>
  )
}

export const AutoComplete = withFormikSupport(AutoCompleteComponent)
